/* LUCRÜ™ — © 2026 LUCRÜ. All rights reserved. */
/**
 * Voice Cloning — upload a 30s sample, create an ElevenLabs IVC voice,
 * and bind it to the current session so Lyra reads chat in the streamer's voice.
 * Lives inside the Settings panel.
 */
import { useRef, useState } from "react";
import { toast } from "sonner";
import { MicrophoneIcon, UploadSimpleIcon, TrashIcon, SpinnerIcon } from "@phosphor-icons/react";
import { api, BACKEND_URL } from "@/lib/api";

const VoiceCloneUpload = ({ sessionId, settings, onUpdate }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState(settings?.cloned_voice_name || "My voice");

  const cloned = !!settings?.cloned_voice_id;

  const pickFile = () => fileRef.current?.click();

  const upload = async (file) => {
    if (!file) return;
    if (!settings?.elevenlabs_api_key) {
      toast.error("Add your ElevenLabs API key first");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Sample too big — keep it under 10 MB (30s is plenty)");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("session_id", sessionId);
      fd.append("name", name || "My voice");
      fd.append("sample", file, file.name);
      fd.append("api_key", settings.elevenlabs_api_key);
      const res = await fetch(`${BACKEND_URL}/api/voice/clone`, { method: "POST", body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = data?.detail || `Clone failed (${res.status})`;
        throw new Error(detail);
      }
      toast.success(`Voice "${data.voice_name}" cloned 🎙️`);
      onUpdate && onUpdate({ cloned_voice_id: data.voice_id, cloned_voice_name: data.voice_name, voice_id: data.voice_id, voice_name: data.voice_name });
    } catch (e) {
      toast.error(e.message || "Clone failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeClone = async () => {
    try {
      await api.delete(`/voice/clone/${sessionId}`);
      onUpdate && onUpdate({ cloned_voice_id: null, cloned_voice_name: null });
      toast.success("Cloned voice unlinked");
    } catch (e) {
      toast.error("Remove failed");
    }
  };

  return (
    <div className="bg-[#05050A] border border-[#222233] rounded-lg px-3 py-3">
      <div className="flex items-center gap-2 mb-2">
        <MicrophoneIcon size={14} color="#FF007F" weight="fill" />
        <div className="text-sm font-semibold">Clone my voice</div>
      </div>
      <div className="text-xs text-white/50 leading-relaxed mb-3">
        Upload a 30-second clean recording (mp3/wav) of your voice. Lyra will read chat in your own voice.
      </div>
      {cloned ? (
        <div className="flex items-center justify-between bg-[#0F0F16] border border-[#FF007F]/30 rounded-lg px-3 py-2">
          <div className="text-xs">
            <div className="font-semibold text-[#FF007F]">{settings.cloned_voice_name}</div>
            <div className="text-white/40 text-[10px]">Active — Lyra uses your voice</div>
          </div>
          <button onClick={removeClone} data-testid="voice-clone-remove" className="text-white/40 hover:text-[#FF007F]" title="Unlink">
            <TrashIcon size={14} />
          </button>
        </div>
      ) : (
        <>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Voice name (e.g. My voice)"
            data-testid="voice-clone-name"
            className="w-full bg-[#0F0F16] border border-[#222233] rounded px-2 py-1.5 text-xs mb-2 outline-none focus:border-[#FF007F]"
          />
          <input
            ref={fileRef}
            type="file"
            accept="audio/*"
            onChange={(e) => upload(e.target.files?.[0])}
            className="hidden"
            data-testid="voice-clone-file"
          />
          <button
            onClick={pickFile}
            disabled={busy}
            data-testid="voice-clone-upload"
            className="w-full btn-pill btn-primary justify-center text-xs disabled:opacity-50"
          >
            {busy ? <><SpinnerIcon size={13} className="animate-spin" /> Cloning…</> : <><UploadSimpleIcon size={13} /> Upload sample</>}
          </button>
        </>
      )}
    </div>
  );
};

export default VoiceCloneUpload;
